import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, Activity, Database, TrendingUp, Clock,
  CheckCircle2, XCircle, Loader2, ChevronRight,
} from 'lucide-react';
import { apiKeysAPI } from '@/lib/api';
import { toast } from 'sonner';

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="text-3xl font-bold tracking-tight">{value}</div>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </Card>
  );
}

export default function ApiKeysUsage() {
  const { t } = useTranslation();
  const [keys, setKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [usage, setUsage] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiKeysAPI.list();
        const ks = Array.isArray(r.data) ? r.data : [];
        setKeys(ks);
        if (ks.length > 0) setSelectedKey(ks[0].id);
      } catch {
        toast.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  useEffect(() => {
    if (!selectedKey) return;
    (async () => {
      try {
        const [u, a] = await Promise.all([
          apiKeysAPI.usage(selectedKey),
          apiKeysAPI.activity(selectedKey, 50),
        ]);
        setUsage(u.data);
        setActivity(Array.isArray(a.data) ? a.data : []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || t('common.error'));
        setUsage(null);
        setActivity([]);
      }
    })();
  }, [selectedKey, t]);

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const pctUsed = usage ? (usage.current_hour_usage / usage.rate_limit) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="api-keys-usage-page">
      <div className="flex items-center gap-2">
        <Link to="/settings/api-keys" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-keys-link">
          <ArrowLeft className="w-4 h-4" /> {t('usage.backToKeys')}
        </Link>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="cp-page-title flex items-center gap-2">
            <Activity className="w-6 h-6 text-muted-foreground" />
            {t('usage.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t('usage.subtitle')}</p>
        </div>
        {keys.length > 0 && (
          <Select value={selectedKey} onValueChange={setSelectedKey}>
            <SelectTrigger className="w-full sm:w-72" data-testid="key-selector">
              <SelectValue placeholder="Select a key" />
            </SelectTrigger>
            <SelectContent>
              {keys.map((k) => (
                <SelectItem key={k.id} value={k.id}>
                  {k.name || 'Unnamed'} · {k.prefix}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <Card className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></Card>
      ) : keys.length === 0 ? (
        <Card className="p-8 text-center" data-testid="no-keys-msg">
          <p className="text-muted-foreground">{t('apiKeys.empty')}</p>
          <Link to="/settings/api-keys" className="text-primary hover:underline text-sm mt-2 inline-flex items-center gap-1">
            {t('apiKeys.title')} <ChevronRight className="w-3 h-3" />
          </Link>
        </Card>
      ) : usage ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Activity} label={t('usage.requests24h')} value={usage.requests_24h} testid="stat-24h" />
            <StatCard icon={TrendingUp} label={t('usage.requests7d')} value={usage.requests_7d} testid="stat-7d" />
            <StatCard icon={Clock} label={t('usage.requests30d')} value={usage.requests_30d} testid="stat-30d" />
            <StatCard icon={Database} label={t('usage.recordsImported')} value={usage.records_imported} testid="stat-records" />
          </div>

          <Card className="p-5" data-testid="rate-limit-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold">{t('usage.rateLimitLabel')}</p>
                <p className="text-xs text-muted-foreground">
                  {usage.current_hour_usage} {t('usage.ofLimit', { limit: usage.rate_limit })}
                </p>
              </div>
              <Badge variant={pctUsed > 80 ? 'destructive' : 'default'}
                     className={pctUsed <= 80 ? 'bg-green-600/10 text-green-700 dark:text-green-400 border-green-600/20' : ''}>
                {Math.round(pctUsed)}%
              </Badge>
            </div>
            <Progress value={pctUsed} className="h-2" />
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border text-sm">
              <span className="text-muted-foreground">{t('usage.successRate')}</span>
              <span className="font-semibold text-green-700 dark:text-green-400" data-testid="success-rate">
                {usage.success_rate}%
              </span>
            </div>
          </Card>

          <Card className="p-0 overflow-hidden" data-testid="activity-card">
            <div className="p-5 border-b border-border">
              <h2 className="font-semibold">{t('usage.recentActivity')}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">{t('usage.timestamp')}</th>
                    <th className="px-4 py-3 font-medium">{t('usage.ip')}</th>
                    <th className="px-4 py-3 font-medium hidden md:table-cell">{t('usage.userAgent')}</th>
                    <th className="px-4 py-3 font-medium text-right">{t('usage.records')}</th>
                    <th className="px-4 py-3 font-medium text-center">{t('usage.result')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {activity.length === 0 ? (
                    <tr><td colSpan="5" className="px-4 py-10 text-center text-muted-foreground" data-testid="no-activity-msg">
                      {t('usage.noActivity')}
                    </td></tr>
                  ) : activity.map((e, i) => (
                    <tr key={i} data-testid={`activity-row-${i}`} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{fmt(e.timestamp)}</td>
                      <td className="px-4 py-3 font-mono text-xs">{e.ip || '—'}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground truncate max-w-xs hidden md:table-cell">{e.user_agent || '—'}</td>
                      <td className="px-4 py-3 text-right font-semibold">{e.records_count}</td>
                      <td className="px-4 py-3 text-center">
                        {e.success ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400">
                            <CheckCircle2 className="w-3.5 h-3.5" /> {t('usage.successful')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-destructive">
                            <XCircle className="w-3.5 h-3.5" /> {t('usage.failed')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      ) : null}
    </div>
  );
}
